"use client";

/**
 * ToolError - Error display component
 *
 * Displays tool errors with optional retry action
 */

import { cn } from "@/lib/utils";
import { Alert } from "@/ui";
import { textStyles } from "@/ui/tokens";

/**
 * Extract a clean, human-readable error message from raw API errors.
 * Strips JSON stack traces, SQL details, and other noise.
 */
export function cleanErrorMessage(raw: string): string {
  if (!raw) return "An unknown error occurred";

  // If the message contains a JSON body with stack trace, extract just the message
  const jsonMsgMatch = raw.match(/"message"\s*:\s*"(SQLSTATE[^"]*?)"/);
  if (jsonMsgMatch) {
    // Extract just the SQLSTATE prefix description
    const sqlMsg = jsonMsgMatch[1];
    const readablePart = sqlMsg.match(/SQLSTATE\[\w+\]:\s*([^(]+)/);
    if (readablePart) {
      return readablePart[1].trim();
    }
  }

  // Strip everything after "trace" key in JSON
  const traceIdx = raw.indexOf('"trace"');
  if (traceIdx > 0) {
    const truncated = raw.substring(0, traceIdx).replace(/,\s*$/, "").trim();
    // Try to extract just the message field
    const msgMatch = truncated.match(/"message"\s*:\s*"([^"]+)"/);
    if (msgMatch) return msgMatch[1];
    return truncated.length > 200 ? truncated.substring(0, 200) + "..." : truncated;
  }

  // If it starts with "Failed to create/update..." and has a nested error, extract the outer part
  const nestedMatch = raw.match(/^(Failed to \w+ \w+):.+\((\d+)\):/);
  if (nestedMatch) {
    return `${nestedMatch[1]} (server error ${nestedMatch[2]})`;
  }

  // Cap very long messages
  if (raw.length > 300) {
    return raw.substring(0, 300) + "...";
  }

  return raw;
}

export interface ToolErrorProps {
  error: string;
  onRetry?: () => void;
  className?: string;
  title?: string;
  showIcon?: boolean;
}

export function ToolError({
  error,
  onRetry,
  className,
  title = "Error",
}: ToolErrorProps) {
  return (
    <Alert
      variant="error"
      title={title}
      className={cn(className)}
      action={
        onRetry
          ? { label: "Retry", onClick: onRetry }
          : undefined
      }
    >
      <span style={{ ...textStyles.body2, whiteSpace: "pre-wrap" }}>
        {cleanErrorMessage(error)}
      </span>
    </Alert>
  );
}

export default ToolError;
